import type {
  CreateJournalEntryInput,
  JournalSearchInput,
  ProductJournalEntry,
  RecentJournalFeedEntry,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeJournalEntry = (value: unknown) => ProductJournalEntry;
type NormalizeRecentJournalFeedEntry = (
  value: unknown,
) => RecentJournalFeedEntry;

interface JournalApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeJournalEntry: NormalizeJournalEntry;
  normalizeRecentJournalFeedEntry: NormalizeRecentJournalFeedEntry;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class JournalApiModule {
  constructor(
    private readonly context: Pick<WarehouseApiModuleContext, "sendJson">,
    private readonly deps: JournalApiModuleDependencies,
  ) {}

  async getJournalEntriesByProduct(
    productId: string,
    limit = 50,
  ): Promise<ProductJournalEntry[]> {
    return this.searchJournalEntries({
      productId,
      limit,
    });
  }

  async getJournalEntriesByBox(
    boxId: string,
    limit = 50,
  ): Promise<ProductJournalEntry[]> {
    return this.searchJournalEntries({
      fromTo: boxId,
      limit,
    });
  }

  async getRecentJournalEntries(limit = 20): Promise<ProductJournalEntry[]> {
    return this.searchJournalEntries({ limit });
  }

  async getRecentJournalFeed(limit = 20): Promise<RecentJournalFeedEntry[]> {
    const safeLimit = Number.isFinite(limit)
      ? Math.max(1, Math.floor(limit))
      : 20;
    const query = new URLSearchParams();
    query.set("limit", String(safeLimit));
    const path = `/api/journals/recent-feed?${query.toString()}`;
    const payload = await this.context.sendJson<unknown>(path, "GET");

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке ленты операций",
      );
    }

    return payload.data.map((item) =>
      this.deps.normalizeRecentJournalFeedEntry(item),
    );
  }

  async searchJournalEntries(
    input: JournalSearchInput = {},
  ): Promise<ProductJournalEntry[]> {
    const query = new URLSearchParams();

    if (input.action) query.set("action", input.action);
    if (input.productId) query.set("productId", input.productId.trim());
    if (input.fromTo) query.set("fromTo", input.fromTo.trim());
    if (input.userId) query.set("userId", input.userId.trim());
    if (input.userRole) query.set("userRole", input.userRole);
    if (typeof input.limit === "number")
      query.set("limit", String(input.limit));
    if (typeof input.offset === "number") {
      query.set("offset", String(input.offset));
    }

    const path = query.toString()
      ? `/api/journals/search?${query.toString()}`
      : "/api/journals/search";

    const payload = await this.context.sendJson<unknown>(path, "GET");

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке записей журнала",
      );
    }

    return payload.data.map((item) => this.deps.normalizeJournalEntry(item));
  }

  async createJournalEntry(
    input: CreateJournalEntryInput,
  ): Promise<ProductJournalEntry> {
    const payloadToCreate: Record<string, unknown> = {
      action: input.action,
      productId: input.productId,
      fromTo: input.fromTo,
      value: input.value ?? 1,
      comment: input.comment,
      userId: input.userId ?? null,
    };

    if (input.date) {
      payloadToCreate.date = input.date;
    }

    const payload = await this.context.sendJson<unknown>(
      "/api/journals",
      "POST",
      {
        ...payloadToCreate,
      },
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при создании записи журнала",
      );
    }

    return this.deps.normalizeJournalEntry(payload.data);
  }
}
